import React, {
  useEffect, useMemo, useRef, useState,
} from "react";
import { Empty, Pagination, Spin } from "antd";
import moment from "moment";
import Scrollbar from "react-scrollbars-custom";

import { DEFAULT_DATE_FORMAT, PROMPT_STATUS_CONFIG, normalizePromptStatus } from "./utils";

import "./AIAnalyticsPromptSidebar.css";

const DEFAULT_PAGE_SIZE = 10;
const PAGE_SIZE_OPTIONS = [10, 20, 50, 100];

/** Formats a prompt timestamp for sidebar cards. @param {string|null} value - raw timestamp from API */
const formatCardTimestamp = (value) => {
  if (!value) return "";
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format(DEFAULT_DATE_FORMAT) : "";
};

/** Derives a short card title from the first sentence of the prompt. @param {string} promptText - full prompt text */
const getPromptTitle = (promptText) => {
  const [firstLine] = String(promptText || "").split(".");
  return firstLine?.trim() || "Untitled prompt";
};

/** Builds a display label for a session group. @param {Object} session - session object with session_title; @param {number} index - zero-based session index */
const getSessionLabel = (session, index) => {
  const title = String(session.session_title || "").trim();
  if (title) return title;
  return `Session ${index + 1}`;
};

/** Renders the recent prompts sidebar grouped by session. @param {Array} sessions - session history from API; @param {boolean} isLoading - shows spinner while sessions load; @param {string|null} selectedPromptKey - active card key; @param {Function} onPromptSelect - called with (sessionId, promptId) */
export default function AIAnalyticsPromptSidebar({
  sessions = [],
  isLoading = false,
  selectedPromptKey = null,
  onPromptSelect,
}) {
  const scrollbarRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const sessionGroups = useMemo(() => (
    sessions.map((session, sessionIndex) => ({
      key: session.session_id,
      label: getSessionLabel(session, sessionIndex),
      username: session.user_name,
      prompts: (session.prompts || []).map((prompt, promptId) => ({
        key: `${session.session_id || sessionIndex}-${String(prompt.prompt_id || promptId)}`,
        sessionId: session.session_id,
        promptId: prompt.prompt_id,
        title: getPromptTitle(prompt.prompt),
        timestamp: prompt.timestamp,
        username: prompt.user_name,
        statusConfig: PROMPT_STATUS_CONFIG[normalizePromptStatus(prompt.status)],
        session,
        prompt,
      })),
    }))
  ), [sessions]);

  const allPromptItems = useMemo(() => (
    sessionGroups.flatMap((group) => group.prompts.map((item) => ({
      ...item,
      groupUsername: group.username,
    })))
  ), [sessionGroups]);

  const paginatedPromptItems = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    return allPromptItems.slice(startIndex, startIndex + pageSize);
  }, [allPromptItems, currentPage, pageSize]);

  useEffect(() => {
    setCurrentPage(1);
  }, [sessions]);

  const isEmpty = useMemo(() => allPromptItems.length === 0, [allPromptItems]);

  /** Returns click handler that loads a prompt unless it is already selected. @param {Object} item - normalized sidebar card item */
  const handleCardClick = (item) => () => {
    if (selectedPromptKey === item.key) return;
    if (item.sessionId) {
      onPromptSelect(item.sessionId, item.promptId);
    }
  };

  /** Scrolls the sidebar list to the top when the page changes. @param {number} page - selected page */
  const handlePageChange = (page) => {
    setCurrentPage(page);
    if (scrollbarRef.current) {
      scrollbarRef.current.scrollToTop();
    }
  };

  /** Updates page size, resets to page 1, and scrolls the list to the top. @param {number} _current - current page; @param {number} nextPageSize - selected page size */
  const handlePageSizeChange = (_current, nextPageSize) => {
    setPageSize(nextPageSize);
    setCurrentPage(1);
    if (scrollbarRef.current) {
      scrollbarRef.current.scrollToTop();
    }
  };

  /** Renders a single prompt card in the sidebar list. @param {Object} item - normalized sidebar card item */
  const renderPromptCard = (item) => (
    <div
      key={item.key}
      className={`AIAnalyticsPromptSidebar__Card${selectedPromptKey === item.key ? " AIAnalyticsPromptSidebar__Card--Active" : ""}`}
      onClick={handleCardClick(item)}
    >
      <div className="AIAnalyticsPromptSidebar__CardHeader">
        <span className="AIAnalyticsPromptSidebar__CardTitle">{item.title}</span>
        <div className="AIAnalyticsPromptSidebar__CardActions">
          {item.statusConfig && (
            <span
              className={`AIAnalyticsPromptSidebar__Status AIAnalyticsPromptSidebar__Status--${item.statusConfig.className}`}
            >
              {item.statusConfig.label}
            </span>
          )}
        </div>
      </div>
      <div className="AIAnalyticsPromptSidebar__CardMeta">
        {item.timestamp && (
          <span className="AIAnalyticsPromptSidebar__Pill">
            {formatCardTimestamp(item.timestamp)}
          </span>
        )}
        {(item.groupUsername || item.username) && (
          <span className="AIAnalyticsPromptSidebar__Pill">
            {item.groupUsername || item.username}
          </span>
        )}
      </div>
    </div>
  );

  /** Renders loading, empty, or grouped prompt card content for the sidebar. */
  const renderSidebarContent = () => {
    if (isLoading) {
      return (
        <div className="AIAnalyticsPromptSidebar__Loading">
          <Spin />
        </div>
      );
    }

    if (isEmpty) {
      return (
        <Empty
          className="AIAnalyticsPromptSidebar__Empty"
          description="No recent prompts"
        />
      );
    }

    return (
      <div className="AIAnalyticsPromptSidebar__CardList">
        {paginatedPromptItems.map(renderPromptCard)}
      </div>
    );
  };

  return (
    <aside className="AIAnalyticsPromptSidebar">
      <div className="AIAnalyticsPromptSidebar__Title">Recent Prompts</div>
      <Scrollbar
        ref={scrollbarRef}
        disableTrackYWidthCompensation
        disableTracksWidthCompensation
        className="AIAnalyticsPromptSidebar__Scrollbar"
      >
        {renderSidebarContent()}
      </Scrollbar>
      {!isLoading && !isEmpty && (
        <Pagination
          className="AIAnalyticsPromptSidebar__Pagination"
          current={currentPage}
          pageSize={pageSize}
          total={allPromptItems.length}
          showSizeChanger
          pageSizeOptions={PAGE_SIZE_OPTIONS}
          onChange={handlePageChange}
          onShowSizeChange={handlePageSizeChange}
          size="small"
          align="end"
        />
      )}
    </aside>
  );
}
